import React, { useEffect, useState } from "react";
import { ArrowRight, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";

const show = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

export default function DictationPreview({ open, transcript, changes, busy, onReanalyze, onApply, onClose }) {
  const [text, setText] = useState(transcript || "");
  const [checked, setChecked] = useState({});
  useEffect(() => setText(transcript || ""), [transcript]);
  useEffect(() => setChecked(Object.fromEntries((changes || []).map((c) => [`${c.kind}:${c.key}`, true]))), [changes]);

  const selected = (changes || []).filter((c) => checked[`${c.kind}:${c.key}`]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="dictation-preview">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="w-4 h-4 text-norea-primary" /> Dictée intelligente</DialogTitle>
          <DialogDescription>Vérifiez les valeurs proposées, décochez ce qui ne convient pas, puis validez.</DialogDescription>
        </DialogHeader>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-norea-textMuted mb-1">Ce que vous avez dit</div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} className="w-full text-sm px-3 py-2 border border-norea-border rounded bg-norea-bg/60 outline-none focus:border-norea-primary" data-testid="dictation-transcript" />
          <button onClick={() => onReanalyze(text)} disabled={busy || !text.trim()} className="mt-1 text-xs text-norea-textMuted hover:text-norea-primary inline-flex items-center gap-1 disabled:opacity-50" data-testid="dictation-reanalyze-btn">
            <RefreshCw className="w-3 h-3" /> Ré-analyser le texte corrigé
          </button>
        </div>

        <div className="max-h-[45vh] overflow-y-auto border border-norea-border rounded divide-y divide-norea-border" data-testid="dictation-changes">
          {busy ? (
            <div className="p-6 text-sm text-norea-textMuted flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Analyse en cours…</div>
          ) : (changes || []).length === 0 ? (
            <div className="p-6 text-sm text-norea-textMuted italic text-center" data-testid="dictation-no-change">Aucune information à enregistrer n'a été reconnue dans cette dictée.</div>
          ) : (
            changes.map((c, i) => {
              const id = `${c.kind}:${c.key}`;
              return (
                <label key={id} className="flex items-start gap-3 px-4 py-2.5 cursor-pointer hover:bg-norea-surfaceHover" data-testid={`dictation-change-${i}`}>
                  <Checkbox checked={!!checked[id]} onCheckedChange={(v) => setChecked({ ...checked, [id]: !!v })} className="mt-0.5" data-testid={`dictation-change-check-${i}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-norea-text">{c.label}</div>
                    <div className="text-sm flex items-center gap-2 flex-wrap">
                      <span className="text-norea-textMuted line-through decoration-norea-textDisabled">{show(c.old)}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-norea-textDisabled" />
                      <span className="font-medium text-emerald-800 whitespace-pre-wrap">{show(c.new_label || c.new)}</span>
                    </div>
                  </div>
                </label>
              );
            })
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost text-sm" data-testid="dictation-cancel-btn">Annuler</button>
          <button onClick={() => onApply(selected)} disabled={busy || selected.length === 0} className="btn-primary text-sm disabled:opacity-50" data-testid="dictation-apply-btn">
            Enregistrer {selected.length > 0 ? `(${selected.length})` : ""}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
